import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { BRAND } from '@br/protocol';
import { errorMessage, isApiError } from '../api/client';
import { useLogin, useMe } from '../api/queries';
import { Alert, Button, Field, FullPageSpinner, Input } from '../components/ui';
import { usePageTitle } from '../lib/title';

export function LoginPage() {
  usePageTitle('로그인');
  const me = useMe();
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (me.isPending) return <FullPageSpinner />;
  if (me.data) return <Navigate to={from && from !== '/login' ? from : '/profiles'} replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    login.mutate(
      { email: email.trim(), password },
      {
        onSuccess: () =>
          navigate(from && from !== '/login' ? from : '/profiles', { replace: true }),
        onError: (err) =>
          setError(
            isApiError(err) && err.status === 401
              ? '이메일 또는 비밀번호가 올바르지 않습니다'
              : errorMessage(err),
          ),
      },
    );
  };

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-line bg-surface p-8 shadow-sm"
      >
        <h1 className="text-xl font-bold text-ink">{BRAND.name}</h1>
        <p className="mt-1 text-sm text-ink-muted">운영자 로그인</p>
        <div className="mt-6 space-y-4">
          <Field label="이메일" required>
            <Input
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="비밀번호" required>
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          {error && <Alert level="error">{error}</Alert>}
          <Button type="submit" variant="primary" className="w-full" loading={login.isPending}>
            로그인
          </Button>
        </div>
        <p className="mt-6 text-xs text-ink-muted">
          계정이 없다면 관리자에게 초대 링크를 요청하세요.
        </p>
      </form>
    </div>
  );
}
